
import { HttpParams } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { select } from '@ngrx/store';
import { InputFieldComponent } from 'src/app/components/form/input-field/input-field.component';
import { SelectFieldComponent } from 'src/app/components/form/select-field/select-field.component';
import { AlertInfo } from 'src/app/models/alert-info';
import { SavedDataInfo } from 'src/app/models/form/saved-data-info';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { ProjectsService } from 'src/app/services/api/projects.service';
import { FormService } from 'src/app/services/form.service';
import { ValidatorService } from 'src/app/services/validator.service';

const params = new HttpParams().set('size', '9999');

@Component({
  selector: 'settings-projects-testlist-form',
  imports: [InputFieldComponent, SelectFieldComponent, NgbAlert, ReactiveFormsModule],
  templateUrl: './settings-projects-testlist-form.component.html',
  styleUrl: './settings-projects-testlist-form.component.scss'
})
export class SettingsProjectsTestlistFormComponent implements OnInit {

  @Input() testlist?: any;
  @Output() onSavedData = new EventEmitter<SavedDataInfo>();
  @Output() onFieldChange = new EventEmitter<{ jurisdiction: number; law: number; checklist?: number }>();

  form!: FormGroup;

  alertInfo: AlertInfo = { show: false, type: '', message: '' };

  jurisdictionsOptions: { key: number; value: string }[] = [];

  lawsOptions: { key: number; value: string }[] = [];

  checklistOptions: { key: number; value: string }[] = [];


  constructor(
    private validatorService: ValidatorService,
    private formService: FormService,
    private alertNotificationService: AlertNotificationService,
    private projectsService: ProjectsService,
  ) {
    this.projectsService.jurisdictions$.subscribe({
      next: (jurisdictions: any) => {
        this.jurisdictionsOptions = jurisdictions.map((jurisdiction: any) => ({ 
          key: jurisdiction.jurisdictionId, 
          value: jurisdiction.name 
        }));
      },
      error: (error) => console.error('Error loading jurisdictions:', error)
    });
    
    this.projectsService.laws$.subscribe({
      next: (laws: any) => {
        this.lawsOptions = laws.map((law: any) => ({ 
          key: law.lawId , 
          value: law.name 
        }));
      },
      error: (error) => console.error('Error loading laws:', error)
    });
    
    this.checklistOptions = [];
  }


  ngOnInit(): void {
    this.projectsService.getLaws();
    
    this.form = new FormGroup({
      jurisdiction: new FormControl('', [Validators.required]),
      law: new FormControl({value: '', disabled: true}, [Validators.required]),
      checklist: new FormControl({value: '', disabled: true}, [Validators.required]),
      details: new FormControl('', [Validators.required]),
    });

    this.form.get('jurisdiction')?.valueChanges.subscribe(selectedJurisdiction => {
      if (selectedJurisdiction) {
        this.form.get('law')?.setValue('');
        this.form.get('checklist')?.setValue('');
        
        this.form.get('law')?.enable();
        
        const jurisdictionId = typeof selectedJurisdiction === 'string' 
          ? parseInt(selectedJurisdiction, 10) 
          : selectedJurisdiction;
          
        this.loadChecklistsByJurisdictionAndLaw(jurisdictionId);
        
        this.projectsService.getLawsByJurisdiction(selectedJurisdiction).subscribe({
          next: (laws: any[]) => {
            if (laws && laws.length > 0) {
              if (laws[0].key !== undefined && laws[0].value !== undefined) {
                this.lawsOptions = laws;
              } else {
                this.lawsOptions = laws.map(law => ({ 
                  key: law.lawId || law.id, 
                  value: law.name 
                }));
              }              
              this.form.get('checklist')?.enable();
            } else {
              this.lawsOptions = [];
              console.warn('No laws found for jurisdiction:', selectedJurisdiction);
            }
          },
          error: (error) => {
            console.error('Error loading laws for jurisdiction:', selectedJurisdiction, error);
            this.alertNotificationService.error('Failed to load laws for the selected jurisdiction');
            this.lawsOptions = [];
          }
        });
      } else {
        this.form.get('law')?.disable();
        this.form.get('checklist')?.disable();
      }
    });
    
    this.form.get('law')?.valueChanges.subscribe((lawValue) => {
      const jurisdiction = this.form.get('jurisdiction')?.value;
      
      if (jurisdiction && lawValue) {
        const jurisdictionId = typeof jurisdiction === 'string' ? parseInt(jurisdiction, 10) : jurisdiction;
        const lawId = typeof lawValue === 'string' ? parseInt(lawValue, 10) : lawValue;
        this.loadChecklistsByJurisdictionAndLaw(jurisdictionId, lawId);
        this.form.get('checklist')?.setValue('');
      }
    });
    this.form.get('checklist')?.valueChanges.subscribe((checklistValue) => {
      const jurisdiction = this.form.get('jurisdiction')?.value;
      const law = this.form.get('law')?.value;      
      if (jurisdiction && law && checklistValue) {
        const jurisdictionId = typeof jurisdiction === 'string' ? parseInt(jurisdiction, 10) : jurisdiction;
        const lawId = typeof law === 'string' ? parseInt(law, 10) : law;
        const checklistId = typeof checklistValue === 'string' ? parseInt(checklistValue, 10) : checklistValue;
        
        this.onFieldChange.emit({ 
          jurisdiction: jurisdictionId,  
          law: lawId,
          checklist: checklistId
        });
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['testlist'] && changes['testlist'].currentValue) {      
      const jurisdictionId = changes['testlist'].currentValue.jurisdictionId;
      const lawId = changes['testlist'].currentValue.lawId;
      
      this.projectsService.getLawsByJurisdiction(jurisdictionId).subscribe({
        next: (laws: any[]) => {
          if (laws && laws.length > 0) {
            if (laws[0].key !== undefined && laws[0].value !== undefined) {
              this.lawsOptions = laws;
            } else {
              this.lawsOptions = laws.map(law => ({ 
                key: law.lawId || law.id, 
                value: law.name 
              }));
            }
            
            if (jurisdictionId) {
              if (lawId) {
                this.loadChecklistsByJurisdictionAndLaw(jurisdictionId, lawId);
              } else {
                this.loadChecklistsByJurisdictionAndLaw(jurisdictionId);
              }
            }
            
            setTimeout(() => {
              if (jurisdictionId) {
                this.form.get('law')?.enable();
                this.form.get('checklist')?.enable();
              }
              
              this.form.patchValue({
                jurisdiction: jurisdictionId,
                law: lawId,
                checklist: changes['testlist'].currentValue.checklists[0].checklistId,
                details: changes['testlist'].currentValue.details,
              });
              
              const checklistId = changes['testlist'].currentValue.checklistId || changes['testlist'].currentValue.name;
              if (jurisdictionId && lawId && checklistId) {
                this.onFieldChange.emit({
                  jurisdiction: jurisdictionId,
                  law: lawId,
                  checklist: typeof checklistId === 'string' ? parseInt(checklistId, 10) : checklistId
                });
              }
            }, 100);
          } else {
            console.warn('No laws found for testlist jurisdiction:', jurisdictionId);
            this.form.patchValue({
              jurisdiction: jurisdictionId,
              checklist: changes['testlist'].currentValue.name,
              details: changes['testlist'].currentValue.details,
            });
          }
        },
        error: (error) => {
          console.error('Error loading laws for testlist jurisdiction:', error);
    
          this.form.patchValue({
            jurisdiction: jurisdictionId,
            checklist: changes['testlist'].currentValue.name,
            details: changes['testlist'].currentValue.details,
          });
        }
      });
    }
  }
  loadChecklistsByJurisdictionAndLaw(jurisdiction: number, law?: number): void {
    const filters: { jurisdictionId: number; lawId?: number } = { 
      jurisdictionId: jurisdiction
    };
    
    if (law) {
      filters.lawId = law;
    }
    
    let queryParams = new HttpParams().set('size', '9999')
                                      .set('jurisdictionId', jurisdiction.toString());
                                      
    if (law) {
      queryParams = queryParams.set('lawId', law.toString());
    }
    
    this.projectsService.listSettingsChecklist({ params: queryParams }).subscribe({
      next: (response) => {
        
        if (response && response.data && response.data.checklists) {
          this.checklistOptions = response.data.checklists.map((checklist: any) => ({
            key: checklist.checklistId,
            value: checklist.name
          }));
        } else {
          this.checklistOptions = [];
        }
        this.form.get('checklist')?.setValue('');
      },
      error: (error) => {
        console.error('Error loading checklists with filters:', error);
        this.alertNotificationService.error('Failed to load checklists');
        this.checklistOptions = [];
      }
    });
  }

  /**
     * *******************
     * Submit form methods
     * *******************
     */
  showAlertInfo(type: string, message: string) {
    this.alertInfo = {
      show: true,
      type,
      message
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }
  updateValidationErrors(validationErrors: any) {
    this.formService.resetErrors(this.form);
    this.formService.setValidationErrors(this.form, validationErrors);
  }
  submitFormSuccess(response: any) {
    this.alertNotificationService.success('Operation completed successfully')
    this.showAlertInfo('success', 'Operation completed successfully');

    this.onSavedData.emit({
      success: true,
      response
    });
  }
  submitFormError(error: any) {

    if (error.error.error.description == 'ARGUMENT_VALIDATION_ERROR') {
      this.updateValidationErrors(error.error.error.validationErrors);
    }
    else if (error.error.error.description == 'USER_EMAIL_ALREADY_EXISTS') {
      this.updateValidationErrors([{ field: 'email', defaultMessage: error.error.error.message }]);
      this.showAlertInfo('danger', `Please fix the error: ${error.error.error.message}`);
    }
    else {
      this.showAlertInfo('danger', error.error.error.message);
    }

    this.onSavedData.emit({
      success: false,
      error
    });

  }  submitForm(): void {
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      control?.markAsTouched();
      control?.updateValueAndValidity();
    });    
    this.emitSelectedValues();

    if (this.form.valid) {
      const values = Object.assign(
        this.form.value,
        {
          tenantId: 1,
        },
      )

      this.alertInfo.show = false;
      this.formService.resetErrors(this.form);

      const serviceMethod = this.testlist
        ? this.projectsService.updateSettingsTestlist(this.testlist.testlist_id, values)
        : this.projectsService.createSettingsTestlist(values);

      serviceMethod.subscribe({
        next: (response: any) => {
          this.submitFormSuccess(response);
        },
        error: (error: any) => {
          this.submitFormError(error);
        },
      });
    }
  }
  /**
   * *******************
   * Submit form methods
   * *******************
   */

  getProjectJurisdictionSelect(): FormControl { return this.getFormControl('jurisdiction'); }
  getProjectLawSelect(): FormControl { return this.getFormControl('law'); }
  getProjectChecklistSelect(): FormControl { return this.getFormControl('checklist'); }
  getProjectDetailsInput(): FormControl { return this.getFormControl('details'); }

  getFormControl(name: string): FormControl { return this.form.get(name) as FormControl; }

  emitSelectedValues(): void {
    const jurisdiction = this.form.get('jurisdiction')?.value;
    const law = this.form.get('law')?.value;
    const checklist = this.form.get('checklist')?.value;
    
    if (jurisdiction && law && checklist) {
      const jurisdictionId = typeof jurisdiction === 'string' ? parseInt(jurisdiction, 10) : jurisdiction;
      const lawId = typeof law === 'string' ? parseInt(law, 10) : law;
      const checklistId = typeof checklist === 'string' ? parseInt(checklist, 10) : checklist;
      this.onFieldChange.emit({ 
        jurisdiction: jurisdictionId, 
        law: lawId,
        checklist: checklistId
      });
    }
  }
}
